import { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocation } from "react-router-dom";

import type { StepMap, StepRenderProps } from "@/components/onboarding/components/welcome-wizard";
import { CommercialPlanSelectorModal } from "@/components/commercial-plan-selector";
import {
  FooterContainer,
  FooterSkipButton,
  StepContent,
  StepTitle,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard";
import { useOnboardingSystemPermissionsHandlers } from "@/components/onboarding/components/welcome-wizard/shared";
import { PushToRememberDesktopGuidance } from "@/components/onboarding/components/welcome-wizard/steps/mem-agent/push-to-remember-desktop-guidance";
import { OnboardingPermissionsRestartScreen } from "@/components/onboarding/components/welcome-wizard/steps/system-permission-voice/components";
import { OnboardingVoicePermissionsScreen } from "@/components/onboarding/components/welcome-wizard/steps/system-permission-voice/components";
import { ReferrerStep } from "@/components/onboarding/components/welcome-wizard/steps/icp/referrer-step";
import { startProactiveCheckout } from "@/components/modal/proactive-upsell/proactive-checkout";
import { IntentFork, type OnboardingIntent } from "@/components/proactive-trial/intent-fork";
import { MeetingTrialPitch } from "@/components/proactive-trial/meeting-trial-pitch";
import { QuickThoughtsTrialPitch } from "@/components/proactive-trial/quick-thoughts-trial-pitch";
import { buildProactivePaywallEventPayload } from "@/domains/metrics/monetization";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import { clientEnvModule } from "@/modules/client-env";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { StripeCheckoutSource } from "@/modules/subscriptions/query-params";
import { subscriptionModule } from "@/modules/subscriptions";
import { toastModule } from "@/modules/toast";
import { type AppStore, useAppStore } from "@/store";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import {
  CommercialPlanKey,
  getCompatibleCommercialCatalog,
  isCommercialRolloutActive,
  type CommercialPlanDefinition,
} from "@/modules/commercial-plans";

const QUICK_THOUGHT_CAPTURE_SKIP_DELAY_MS = 35_000;

export type IntentCheckoutRevalidation = "allowed" | "blocked" | "unavailable";

/** Prevents a stale onboarding screen from opening a second cross-device checkout. */
export const revalidateIntentCheckout = async (
  store: AppStore
): Promise<IntentCheckoutRevalidation> => {
  let didRefresh = false;
  try {
    didRefresh = await store.platformInfo.fetchPlatformInfo({
      reconcileStripeSubscription: true,
    });
  } catch (error) {
    logger.warn({
      message: "[IntentOnboarding] Checkout revalidation failed closed",
      info: { error: objectModule.safeErrorAsJson(error as Error) },
    });
  }
  if (!didRefresh) {
    return "unavailable";
  }

  if (
    store.platformInfo.onboardingInfo.allTasksCompletedAt !== null ||
    store.subscriptions.hasProactiveAccess
  ) {
    return "blocked";
  }

  return "allowed";
};

const IntentPickerStep = observer((_props: StepRenderProps) => {
  const { store } = useAppStore();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    trackEvent(TrackedEvent.IntentOnboardingPickerView, {
      onboarding_flow_version: "intent_v1",
    });
  }, []);

  const handleContinue = useCallback(
    async (intent: OnboardingIntent) => {
      if (isSaving) return;
      setIsSaving(true);
      trackEvent(TrackedEvent.IntentOnboardingIntentSelect, {
        onboarding_flow_version: "intent_v1",
        onboarding_intent: intent,
      });
      try {
        await store.platformInfo.setIntentOnboardingIntent(intent);
      } catch (error) {
        logger.error({
          message: "[IntentOnboarding] Failed to save intent",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
        toastModule.triggerToast({
          content: "We couldn't save that choice. Please try again.",
        });
        setIsSaving(false);
      }
    },
    [isSaving, store.platformInfo]
  );

  return (
    <IntentFork
      initialIntent={store.platformInfo.onboardingInfo.onboardingIntent}
      isPending={isSaving}
      onContinue={handleContinue}
    />
  );
});

const IntentQuickThoughtPermissionsStep = observer(({ onComplete }: StepRenderProps) => {
  const { store } = useAppStore();
  const {
    resolveConfigurationStatus,
    handleEnablePermission,
    handleOpenPermissionPane,
    restartRequired,
  } = useOnboardingSystemPermissionsHandlers();
  const isWindows = clientEnvModule.isWindows();

  if (restartRequired) {
    return <OnboardingPermissionsRestartScreen onClose={() => onComplete()} />;
  }

  return (
    <OnboardingVoicePermissionsScreen
      experience="push-to-remember"
      pushToRememberPlatform={isWindows ? "windows" : "mac"}
      showFinish={false}
      restartRequired={restartRequired}
      resolveConfigurationStatus={resolveConfigurationStatus}
      handleEnablePermission={handleEnablePermission}
      handleOpenPermissionPane={handleOpenPermissionPane}
      onNext={() => {
        store.platformInfo.perDeviceOnboardingInfo.setQuickThoughtsCaptureMethod("voice");
        onComplete();
      }}
      onSkip={() => {
        store.platformInfo.perDeviceOnboardingInfo.setQuickThoughtsCaptureMethod("typed");
        onComplete();
      }}
    />
  );
});

const IntentQuickThoughtCaptureStep = observer(({ onComplete }: StepRenderProps) => {
  const { store } = useAppStore();
  const [canSkip, setCanSkip] = useState(false);
  const isWindows = clientEnvModule.isWindows();

  useEffect(() => {
    store.platformInfo.perDeviceOnboardingInfo.activateMemAgentFirstMessageOnboarding();
    const timer = window.setTimeout(() => setCanSkip(true), QUICK_THOUGHT_CAPTURE_SKIP_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [store.platformInfo.perDeviceOnboardingInfo]);

  return (
    <WizardContentWrapper>
      <StepTitle>Tell Mem something to remember</StepTitle>
      <StepContent>
        <PushToRememberDesktopGuidance isWindows={isWindows} />
        <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
          Mem keeps track of what you need to remember. And with Mem Proactive, it&apos;ll smartly
          follow up at the right time, making sure it gets handled. No need to set due dates or set
          up reminders.
        </MDSText>
      </StepContent>
      <FooterContainer>
        <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
          Can&apos;t talk right now? Click the floating Mem Agent icon and type instead.
        </MDSText>
        {canSkip ? (
          <FooterSkipButton onClick={() => onComplete()}>Skip for now</FooterSkipButton>
        ) : null}
      </FooterContainer>
    </WizardContentWrapper>
  );
});

const IntentProactiveTrialStep = observer((_props: StepRenderProps) => {
  const { store } = useAppStore();
  const location = useLocation();
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [commercialPlanMode, setCommercialPlanMode] = useState<"all" | "focused">("focused");
  const intent = store.platformInfo.onboardingInfo.onboardingIntent;
  const commercialPlanSnapshot = store.platformInfo.commercialPlanSnapshot;
  const commercialCatalog = getCompatibleCommercialCatalog({
    catalog: store.platformInfo.commercialCatalog,
    snapshot: commercialPlanSnapshot,
  });
  const commercialRolloutIsActive = isCommercialRolloutActive({
    catalog: store.platformInfo.commercialCatalog,
    snapshot: commercialPlanSnapshot,
  });

  useEffect(() => {
    trackEvent(TrackedEvent.IntentOnboardingTrialPitchView, {
      onboarding_flow_version: "intent_v1",
      onboarding_intent: intent,
    });
  }, [intent]);

  const handleContinueFree = useCallback(() => {
    trackEvent(
      TrackedEvent.PaywallCancelClick,
      buildProactivePaywallEventPayload({ store, source: "onboarding" })
    );
    store.platformInfo.perDeviceOnboardingInfo.completeOrSkipProactiveTrialOnboarding();
  }, [store]);

  const handleContinueToCheckout = useCallback(async () => {
    if (isSubscribing || store.onboarding.proactiveCheckoutReturnConfirmationIsPending) return;
    setIsSubscribing(true);

    const checkoutRevalidation = await revalidateIntentCheckout(store);
    if (checkoutRevalidation !== "allowed") {
      if (checkoutRevalidation === "unavailable") {
        toastModule.triggerToast({
          content: "We couldn't verify your plan. Please try again.",
        });
      }
      setIsSubscribing(false);
      return;
    }

    if (store.subscriptions.shouldUseAppleProactiveUpgradeModal) {
      trackEvent(TrackedEvent.IntentOnboardingTrialStartClick, {
        onboarding_flow_version: "intent_v1",
        onboarding_intent: intent,
        billing_surface: "apple",
      });
      store.subscriptions.openProactiveUpsell();
      setIsSubscribing(false);
      return;
    }

    trackEvent(
      TrackedEvent.PaywallUpgradeClick,
      buildProactivePaywallEventPayload({ store, source: "onboarding" })
    );
    trackEvent(TrackedEvent.IntentOnboardingTrialStartClick, {
      onboarding_flow_version: "intent_v1",
      onboarding_intent: intent,
      billing_surface: "stripe",
    });
    try {
      const result = await startProactiveCheckout({
        checkoutSource: StripeCheckoutSource.Onboarding,
        location,
        store,
      });
      if (!result.didStartExternalNavigation) {
        store.platformInfo.perDeviceOnboardingInfo.completeOrSkipProactiveTrialOnboarding();
      }
    } catch (error) {
      logger.error({
        message: "[IntentOnboarding] Failed to start Proactive checkout",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
      toastModule.triggerToast({
        content: "Failed to start checkout. Please try again.",
      });
      setIsSubscribing(false);
    }
  }, [intent, isSubscribing, location, store]);

  const handleChooseCommercialPlan = useCallback(
    async (plan: CommercialPlanDefinition) => {
      if (isSubscribing || plan.key === CommercialPlanKey.Mem || !plan.checkoutAvailable) {
        return;
      }

      setIsSubscribing(true);
      const checkoutRevalidation = await revalidateIntentCheckout(store);
      if (checkoutRevalidation !== "allowed") {
        if (checkoutRevalidation === "unavailable") {
          toastModule.triggerToast({
            content: "We couldn't verify your plan. Please try again.",
          });
        }
        setIsSubscribing(false);
        return;
      }

      trackEvent(
        TrackedEvent.PaywallUpgradeClick,
        buildProactivePaywallEventPayload({ store, source: "onboarding" })
      );
      try {
        const result = await subscriptionModule.createCheckoutSessionAndNavigate({
          billingStrategy: BillingStrategyEnum.MONTHLY,
          checkoutSource: StripeCheckoutSource.Onboarding,
          commercialPlanKey: plan.key,
          extraUsageBlocks: 0,
          location,
          store,
        });
        if (!result.didStartExternalNavigation) {
          store.platformInfo.perDeviceOnboardingInfo.completeOrSkipProactiveTrialOnboarding();
        }
      } catch (error) {
        logger.error({
          message: "[IntentOnboarding] Failed to start commercial checkout",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
        toastModule.triggerToast({
          content: "Failed to start checkout. Please try again.",
        });
        setIsSubscribing(false);
      }
    },
    [isSubscribing, location, store]
  );

  if (commercialRolloutIsActive && commercialCatalog && commercialPlanSnapshot) {
    return (
      <CommercialPlanSelectorModal
        actionIsLoading={isSubscribing}
        catalog={commercialCatalog}
        detail="Mem Pro adds custom routines and connections so Mem can follow through in the background."
        isOpen
        mode={commercialPlanMode}
        onChoosePlan={plan => void handleChooseCommercialPlan(plan)}
        onClose={handleContinueFree}
        onSecondaryAction={handleContinueFree}
        onShowAllPlans={
          commercialPlanMode === "focused" ? () => setCommercialPlanMode("all") : undefined
        }
        secondaryActionLabel="Continue with Mem"
        snapshot={commercialPlanSnapshot}
        title="Choose how much Mem can take on"
      />
    );
  }

  if (intent === "meetings") {
    return (
      <MeetingTrialPitch
        isCheckoutLoading={isSubscribing}
        onContinueFree={handleContinueFree}
        onContinueToCheckout={handleContinueToCheckout}
      />
    );
  }

  return (
    <QuickThoughtsTrialPitch
      captureMethod={store.platformInfo.perDeviceOnboardingInfo.quickThoughtsCaptureMethod}
      isCheckoutLoading={isSubscribing}
      onContinueFree={handleContinueFree}
      onContinueToCheckout={handleContinueToCheckout}
    />
  );
});

export const IntentOnboardingStepMap: StepMap = {
  "intent-picker": {
    id: "intent-picker",
    ownsModal: true,
    render: props => <IntentPickerStep {...props} />,
  },
  "intent-quick-thought-permissions": {
    id: "intent-quick-thought-permissions",
    render: props => <IntentQuickThoughtPermissionsStep {...props} />,
  },
  "intent-quick-thought-capture": {
    id: "intent-quick-thought-capture",
    layout: "compact-media",
    render: props => <IntentQuickThoughtCaptureStep {...props} />,
  },
  "intent-proactive-trial": {
    id: "intent-proactive-trial",
    ownsModal: true,
    render: props => <IntentProactiveTrialStep {...props} />,
  },
  "intent-referrer": {
    id: "intent-referrer",
    render: props => <ReferrerStep {...props} finishLabel="Open Mem" />,
  },
};
